import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

function StaticSites() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    "headline": "SharePanda Static Site Hosting - Deploy like Surge.sh",
    "description": "Host static websites on SharePanda with a command-line CLI. Free project.sharepanda.org domains, custom domains, and instant rollbacks.",
    "author": {
      "@type": "Organization",
      "name": "SharePanda"
    },
    "url": "https://sharepanda.org/static-sites"
  };

  return (
    <>
      <SEO 
        title="Static Site Hosting | SharePanda"
        description="Deploy static websites from your terminal with SharePanda. Free .sharepanda.org domains, custom domains, release history, and instant rollbacks."
        keywords="static site hosting, surge.sh alternative, CLI deploy, free static hosting, custom domain hosting, SharePanda CLI"
        url="https://sharepanda.org/static-sites"
        jsonLd={jsonLd}
      />
      <div className="docs-page">
        <div className="container">
          <div className="docs-header">
            <h1>Static Site Hosting</h1>
            <p className="docs-subtitle">
              Deploy websites from your terminal — like Surge.sh, but built into your SharePanda project.
            </p>
          </div>

          <section className="docs-section">
            <h2>What you get</h2>
            <ul>
              <li><strong>Free default domain</strong> — <code>https://&lt;project&gt;.sharepanda.org</code></li>
              <li><strong>Custom domains</strong> — bring any domain you own</li>
              <li><strong>Release history & rollback</strong> — every publish is versioned</li>
              <li><strong>CLI-first workflow</strong> — build locally, run <code>sharepanda publish</code></li>
              <li><strong>One site per project</strong> — tied to your SharePanda project</li>
            </ul>
          </section>

          <section className="docs-section">
            <h2>1. Install the CLI</h2>
            <p>The CLI is written in Rust and installs as <code>sharepanda</code>.</p>

            <h3>From source</h3>
            <div className="code-block">
              <div className="code-header"><span>bash</span></div>
              <pre><code>{`git clone https://github.com/sharepanda/sharepanda.git
cd sharepanda/cli/sharepanda
cargo install --path .`}</code></pre>
            </div>

            <h3>Pre-built binaries</h3>
            <p>
              Download the latest release from the{" "}
              <a href="https://github.com/sharepanda/sharepanda/releases" target="_blank" rel="noopener noreferrer">
                GitHub Releases
              </a>{" "}
              page.
            </p>
          </section>

          <section className="docs-section">
            <h2>2. Log in</h2>
            <p>
              Create an API key from your <Link to="/api-keys">API Keys page</Link>, then run:
            </p>
            <div className="code-block">
              <div className="code-header"><span>bash</span></div>
              <pre><code>sharepanda login --api-key YOUR_API_KEY</code></pre>
            </div>
          </section>

          <section className="docs-section">
            <h2>3. Publish a site</h2>
            <p>From your static build directory:</p>
            <div className="code-block">
              <div className="code-header"><span>bash</span></div>
              <pre><code>{`cd ./dist
sharepanda publish --project <PROJECT_ID>`}</code></pre>
            </div>
            <p>
              Your site goes live at <code>https://&lt;project-slug&gt;.sharepanda.org</code>.
              A <code>sharepanda.json</code> file is created so future publishes only need:
            </p>
            <div className="code-block">
              <div className="code-header"><span>bash</span></div>
              <pre><code>sharepanda publish</code></pre>
            </div>
          </section>

          <section className="docs-section">
            <h2>CLI commands</h2>
            <table className="docs-table">
              <thead>
                <tr>
                  <th>Command</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>sharepanda login --api-key &lt;KEY&gt;</code></td>
                  <td>Authenticate and store credentials</td>
                </tr>
                <tr>
                  <td><code>sharepanda publish [PATH]</code></td>
                  <td>Deploy the current or given directory</td>
                </tr>
                <tr>
                  <td><code>sharepanda list</code></td>
                  <td>List all your static sites</td>
                </tr>
                <tr>
                  <td><code>sharepanda versions</code></td>
                  <td>Show release history</td>
                </tr>
                <tr>
                  <td><code>sharepanda rollback &lt;VERSION&gt;</code></td>
                  <td>Roll back to a previous release</td>
                </tr>
                <tr>
                  <td><code>sharepanda teardown &lt;site&gt;</code></td>
                  <td>Delete a static site</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="docs-section">
            <h2>Custom domains</h2>
            <ol>
              <li>Point your domain's <strong>A record</strong> to SharePanda's server IP.</li>
              <li>Publish with the domain flag:</li>
            </ol>
            <div className="code-block">
              <div className="code-header"><span>bash</span></div>
              <pre><code>sharepanda publish --domain example.com</code></pre>
            </div>
            <p>
              <strong>Note:</strong> Default <code>*.sharepanda.org</code> domains get HTTPS automatically.
              Custom domains are served over HTTP.
            </p>
          </section>

          <section className="docs-section">
            <h2>CI / GitHub Actions</h2>
            <div className="code-block">
              <div className="code-header"><span>yaml</span></div>
              <pre><code>{`- name: Build site
  run: npm run build

- name: Publish to SharePanda
  env:
    SHAREPANDA_API_KEY: \${{ secrets.SHAREPANDA_API_KEY }}
  run: |
    cargo install sharepanda || true
    sharepanda login --api-key "$SHAREPANDA_API_KEY"
    sharepanda publish ./dist`}</code></pre>
            </div>
          </section>

          <section className="docs-section">
            <h2>Need help?</h2>
            <p>
              See the <Link to="/docs">API docs</Link> or open an issue on{" "}
              <a href="https://github.com/sharepanda/sharepanda/issues" target="_blank" rel="noopener noreferrer">
                GitHub
              </a>.
            </p>
          </section>
        </div>
      </div>
    </>
  );
}

export default StaticSites;
